/**
 * Task #2903 — Tassonomia causali Nota di Credito / Nota di Debito.
 *
 * Le causali NC/ND sono una classificazione contabile/gestionale SEPARATA
 * dall'asse TipoDocumento: ogni nota di credito è sempre TD04 (semplificata
 * TD08), ogni nota di debito sempre TD05 (semplificata TD09). La causale guida
 * invece l'impatto su magazzino e il testo `Causale` del tracciato.
 *
 * Matrice impatto magazzino (fonte: utente):
 *   - NC reso totale/parziale            → carico
 *   - NC sconto / abbuono / premio/bonus → null
 *   - NC storno (se merce rientrata)     → carico
 *   - ND conguaglio / spese / interessi  → null
 *   - ND merce aggiuntiva                → scarico
 */

import type { MovimentoMagazzino } from './document-catalog';

/** TD di mapping ammessi per le causali (l'asse documento delle note). */
export type CausaleTdMapping = 'TD04' | 'TD05';

export const NC_ND_CAUSALE_IDS = [
  // --- Nota di credito (TD04) ---
  'nc_reso_totale',
  'nc_reso_parziale',
  'nc_sconto_posticipato',
  'nc_abbuono',
  'nc_storno_totale',
  'nc_storno_parziale',
  'nc_premio_bonus',
  'nc_rettifica_iva',
  'nc_errata_fatturazione',
  // --- Nota di debito (TD05) ---
  'nd_conguaglio_prezzo',
  'nd_spese_accessorie',
  'nd_interessi_penali',
  'nd_merce_aggiuntiva',
  'nd_rettifica_iva_aumento',
] as const;
export type NcNdCausaleId = typeof NC_ND_CAUSALE_IDS[number];

export interface NcNdCausale {
  readonly id: NcNdCausaleId;
  readonly td: CausaleTdMapping;
  readonly label: string;
  readonly movimento_magazzino: MovimentoMagazzino;
  /** True se richiede il riferimento al documento collegato (DatiFattureCollegate). */
  readonly richiede_documento_collegato: boolean;
  /** Testo `Causale` di default proposto nel tracciato. */
  readonly causaleDefault: string;
}

export const NC_ND_CAUSALI: ReadonlyArray<NcNdCausale> = [
  // --- Nota di credito (TD04) ---
  {
    id: 'nc_reso_totale', td: 'TD04', label: 'Reso totale',
    movimento_magazzino: 'carico', richiede_documento_collegato: true,
    causaleDefault: 'Reso totale merce di cui alla fattura collegata',
  },
  {
    id: 'nc_reso_parziale', td: 'TD04', label: 'Reso parziale',
    movimento_magazzino: 'carico', richiede_documento_collegato: true,
    causaleDefault: 'Reso parziale merce di cui alla fattura collegata',
  },
  {
    id: 'nc_sconto_posticipato', td: 'TD04', label: 'Sconto posticipato',
    movimento_magazzino: null, richiede_documento_collegato: true,
    causaleDefault: 'Sconto posticipato su fattura collegata',
  },
  {
    id: 'nc_abbuono', td: 'TD04', label: 'Abbuono',
    movimento_magazzino: null, richiede_documento_collegato: true,
    causaleDefault: 'Abbuono su fattura collegata',
  },
  {
    id: 'nc_storno_totale', td: 'TD04', label: 'Storno totale',
    movimento_magazzino: 'carico', richiede_documento_collegato: true,
    causaleDefault: 'Storno totale fattura collegata',
  },
  {
    id: 'nc_storno_parziale', td: 'TD04', label: 'Storno parziale',
    movimento_magazzino: 'carico', richiede_documento_collegato: true,
    causaleDefault: 'Storno parziale fattura collegata',
  },
  {
    id: 'nc_premio_bonus', td: 'TD04', label: 'Premio / Bonus',
    movimento_magazzino: null, richiede_documento_collegato: false,
    causaleDefault: 'Premio/bonus riconosciuto',
  },
  {
    id: 'nc_rettifica_iva', td: 'TD04', label: 'Rettifica IVA',
    movimento_magazzino: null, richiede_documento_collegato: true,
    causaleDefault: 'Rettifica IVA su fattura collegata',
  },
  {
    id: 'nc_errata_fatturazione', td: 'TD04', label: 'Errata fatturazione',
    movimento_magazzino: null, richiede_documento_collegato: true,
    causaleDefault: 'Errata fatturazione documento collegato',
  },

  // --- Nota di debito (TD05) ---
  {
    id: 'nd_conguaglio_prezzo', td: 'TD05', label: 'Conguaglio prezzo',
    movimento_magazzino: null, richiede_documento_collegato: true,
    causaleDefault: 'Conguaglio prezzo su fattura collegata',
  },
  {
    id: 'nd_spese_accessorie', td: 'TD05', label: 'Spese accessorie',
    movimento_magazzino: null, richiede_documento_collegato: true,
    causaleDefault: 'Addebito spese accessorie su fattura collegata',
  },
  {
    id: 'nd_interessi_penali', td: 'TD05', label: 'Interessi / Penali',
    movimento_magazzino: null, richiede_documento_collegato: true,
    causaleDefault: 'Addebito interessi/penali su fattura collegata',
  },
  {
    id: 'nd_merce_aggiuntiva', td: 'TD05', label: 'Merce aggiuntiva',
    movimento_magazzino: 'scarico', richiede_documento_collegato: true,
    causaleDefault: 'Addebito merce aggiuntiva',
  },
  {
    id: 'nd_rettifica_iva_aumento', td: 'TD05', label: 'Rettifica IVA in aumento',
    movimento_magazzino: null, richiede_documento_collegato: true,
    causaleDefault: 'Rettifica IVA in aumento su fattura collegata',
  },
];

export const NC_ND_CAUSALE_BY_ID: ReadonlyMap<NcNdCausaleId, NcNdCausale> =
  new Map(NC_ND_CAUSALI.map((c) => [c.id, c]));

/** Causali della Nota di credito (TD04). */
export const NC_CAUSALI: ReadonlyArray<NcNdCausale> =
  NC_ND_CAUSALI.filter((c) => c.td === 'TD04');

/** Causali della Nota di debito (TD05). */
export const ND_CAUSALI: ReadonlyArray<NcNdCausale> =
  NC_ND_CAUSALI.filter((c) => c.td === 'TD05');

export function isNcNdCausaleId(v: string): v is NcNdCausaleId {
  return (NC_ND_CAUSALE_IDS as readonly string[]).includes(v);
}
